import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiShield,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const PAGE_SIZE = 50;

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function humanize(value) {
  return String(value || "—")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function csvValue(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

export default function AuditLogsPage() {
  const { accessToken } = useAuth();
  const [logs, setLogs] = useState([]);
  const [options, setOptions] = useState({ actions: [], entityTypes: [] });
  const [filters, setFilters] = useState({
    search: "",
    action: "",
    entityType: "",
    from: "",
    to: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    take: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [error, setError] = useState("");

  const queryString = useMemo(() => {
    const query = new URLSearchParams({
      page: String(page),
      take: String(PAGE_SIZE),
    });
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value?.trim()) query.set(key, value.trim());
    });
    return query.toString();
  }, [appliedFilters, page]);

  const loadLogs = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError("");
    try {
      const response = await apiRequest(`/audit-logs?${queryString}`, {
        accessToken,
      });
      setLogs(response?.items || []);
      setPagination(response?.pagination || {});
    } catch (requestError) {
      setLogs([]);
      setPagination({ page: 1, take: PAGE_SIZE, total: 0, totalPages: 1 });
      setError(requestError?.message || "Unable to load the audit log right now.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, queryString]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    async function loadOptions() {
      if (!accessToken) return;
      try {
        const response = await apiRequest("/audit-logs/options", { accessToken });
        setOptions({
          actions: response?.actions || [],
          entityTypes: response?.entityTypes || [],
        });
      } catch {
        setOptions({ actions: [], entityTypes: [] });
      } finally {
        setIsLoadingOptions(false);
      }
    }
    loadOptions();
  }, [accessToken]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    const empty = { search: "", action: "", entityType: "", from: "", to: "" };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const exportCurrentPage = () => {
    if (!logs.length) return;
    const rows = [
      ["Action", "Module", "Reference", "User", "Role", "IP address", "Date & time"],
      ...logs.map((log) => [
        humanize(log.action),
        humanize(log.entityType),
        log.entityId,
        log.actor?.name || "System",
        log.actor?.role || "—",
        log.ipAddress || "—",
        formatDate(log.createdAt),
      ]),
    ];
    const blob = new Blob([rows.map((row) => row.map(csvValue).join(",")).join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `charms-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const start = logs.length ? (pagination.page - 1) * pagination.take + 1 : 0;
  const end = (pagination.page - 1) * pagination.take + logs.length;

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-white to-primary-50/60 p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-primary-700"><FiShield /> System security</p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-950">Audit logs</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600">Live, permission-protected records of actions captured by CHARMS.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={loadLogs} disabled={isLoading} className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:border-primary-200 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"><FiRefreshCw className={isLoading ? "animate-spin" : ""} /> Refresh</button>
            <button type="button" onClick={exportCurrentPage} disabled={!logs.length} className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary-800 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"><FiDownload /> Export current page</button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        <SummaryCard label="Matching events" value={pagination.total || 0} />
        <SummaryCard label="Current page" value={`${pagination.page || 1} / ${pagination.totalPages || 1}`} />
        <SummaryCard label="Records per page" value={pagination.take || PAGE_SIZE} />
      </section>

      <section className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-800"><FiFilter className="text-primary-700" /> Filter audit activity</div>
        <div className="grid gap-3 xl:grid-cols-6">
          <label className="relative xl:col-span-2"><FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} onKeyDown={(event) => event.key === "Enter" && applyFilters()} placeholder="Search action, user or reference" className="h-11 w-full rounded-xl border border-gray-200 pl-10 pr-3 text-sm outline-none transition focus:border-primary-500 focus:ring-4 focus:ring-primary-50" /></label>
          <select value={filters.entityType} disabled={isLoadingOptions} onChange={(event) => setFilters((current) => ({ ...current, entityType: event.target.value }))} className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-primary-500"><option value="">All modules</option>{options.entityTypes.map((type) => <option key={type} value={type}>{humanize(type)}</option>)}</select>
          <select value={filters.action} disabled={isLoadingOptions} onChange={(event) => setFilters((current) => ({ ...current, action: event.target.value }))} className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-primary-500"><option value="">All actions</option>{options.actions.map((action) => <option key={action} value={action}>{humanize(action)}</option>)}</select>
          <input type="date" value={filters.from} onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-500" />
          <input type="date" value={filters.to} onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-500" />
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-3"><button type="button" onClick={clearFilters} className="h-10 rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-600 hover:bg-gray-50">Clear</button><button type="button" onClick={applyFilters} className="h-10 rounded-xl bg-primary-800 px-4 text-sm font-semibold text-white hover:bg-primary-900">Apply filters</button></div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1050px] w-full border-collapse text-sm">
            <thead className="bg-primary-800 text-left text-xs uppercase tracking-wide text-white"><tr><th className="border-r border-primary-700 px-5 py-4 font-semibold">Activity</th><th className="border-r border-primary-700 px-5 py-4 font-semibold">User</th><th className="border-r border-primary-700 px-5 py-4 font-semibold">Module</th><th className="border-r border-primary-700 px-5 py-4 font-semibold">Reference</th><th className="border-r border-primary-700 px-5 py-4 font-semibold">IP address</th><th className="px-5 py-4 font-semibold">Date & time</th></tr></thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? <LoadingRows /> : null}
              {!isLoading && logs.map((log) => <tr key={log.id} className="transition hover:bg-primary-50/40"><td className="border-r border-gray-200 px-5 py-4"><div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl bg-primary-50 text-primary-800"><FiActivity /></span><span className="font-semibold text-gray-900">{humanize(log.action)}</span></div></td><td className="border-r border-gray-200 px-5 py-4"><p className="font-medium text-gray-900">{log.actor?.name || "System"}</p><p className="mt-0.5 text-xs text-gray-500">{log.actor?.employeeCode || log.actor?.role || "System event"}</p></td><td className="border-r border-gray-200 px-5 py-4 text-gray-700">{humanize(log.entityType)}</td><td className="border-r border-gray-200 px-5 py-4 font-mono text-xs text-gray-700">{log.entityId}</td><td className="border-r border-gray-200 px-5 py-4 font-mono text-xs text-gray-600">{log.ipAddress || "—"}</td><td className="px-5 py-4 whitespace-nowrap text-gray-700">{formatDate(log.createdAt)}</td></tr>)}
              {!isLoading && !logs.length ? <tr><td colSpan="6" className="px-6 py-16 text-center text-gray-500">{error || "No audit-log records match these filters."}</td></tr> : null}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between"><span>Showing {start}–{end} of {pagination.total || 0} records</span><div className="flex gap-2"><button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={(pagination.page || 1) <= 1 || isLoading} className="inline-flex h-9 items-center gap-1 rounded-lg border border-gray-200 px-3 font-semibold disabled:cursor-not-allowed disabled:opacity-50"><FiChevronLeft /> Previous</button><button type="button" onClick={() => setPage((current) => Math.min(pagination.totalPages || 1, current + 1))} disabled={(pagination.page || 1) >= (pagination.totalPages || 1) || isLoading} className="inline-flex h-9 items-center gap-1 rounded-lg border border-gray-200 px-3 font-semibold disabled:cursor-not-allowed disabled:opacity-50">Next <FiChevronRight /></button></div></div>
      </section>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"><p className="text-sm text-gray-500">{label}</p><p className="mt-2 text-2xl font-bold text-gray-950">{value}</p></div>;
}

function LoadingRows() {
  return Array.from({ length: 5 }, (_, index) => <tr key={index}><td colSpan="6" className="px-5 py-5"><div className="h-7 animate-pulse rounded-lg bg-gray-100" /></td></tr>);
}
